import type { InSnipCard, Source } from "../types";

/**
 * The Receipt: a card's visible proof, composed strictly from data the card already carries
 * (primary + supporting sources with each outlet's own headline, the trust layer's sourcing
 * stats, and the image host). Nothing here is inferred or invented; when a field cannot be
 * computed from real data it is omitted (fail closed).
 * House style: no em dashes or en dashes anywhere in copy.
 */

export interface ReceiptSource {
  name: string;
  /** The outlet's own headline for this story, its independent framing. */
  title?: string;
  url: string;
  host: string;
  primary: boolean;
}

export type ReceiptStrength = "corroborated" | "sourced" | "single";

export interface Receipt {
  sources: ReceiptSource[];
  /** Unique publishing domains backing the story. */
  independentDomains: number;
  /** Extra sourcing stats when the trust layer carries them. */
  primaryCount?: number;
  attributed?: boolean;
  /** Image licence class when it is derivable from the image host; null when unknown. */
  imageNote: string | null;
  strength: ReceiptStrength;
}

/** Normalise a URL to a comparable host ("www." stripped, lowercased). */
export function receiptHost(url: string | undefined | null): string {
  if (!url) return "";
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h.startsWith("www.") ? h.slice(4) : h;
  } catch {
    return "";
  }
}

const IMAGE_HOST_NOTES: [RegExp, string][] = [
  [/(^|\.)wikimedia\.org$/, "Creative Commons photo via Wikimedia"],
  [/(^|\.)wikipedia\.org$/, "Creative Commons photo via Wikipedia"],
  [/(^|\.)pexels\.com$/, "Free-licence photo via Pexels"],
  [/(^|\.)unsplash\.com$/, "Free-licence photo via Unsplash"],
  [/(^|\.)openverse\.org$/, "Openly licensed photo via Openverse"],
];

/** A licence note for the card image, only when the host itself proves the class. */
export function imageProvenance(imageUrl: string | undefined | null): string | null {
  const host = receiptHost(imageUrl || "");
  if (!host) return null;
  for (const [re, note] of IMAGE_HOST_NOTES) if (re.test(host)) return note;
  // InSnip-hosted rehosts (ImageKit) hide the origin, so no claim is made.
  return null;
}

/** Build the receipt for a card, or null when there is no real source to show. */
export function buildReceipt(
  card: Pick<
    InSnipCard,
    | "primarySourceName"
    | "primarySourceUrl"
    | "supportingSources"
    | "trust"
    | "imageUrl"
  > | null | undefined,
): Receipt | null {
  if (!card) return null;
  const sources: ReceiptSource[] = [];
  const seenUrl = new Set<string>();
  const add = (s: { name?: string; url?: string; title?: string }, primary: boolean) => {
    const url = (s.url || "").trim();
    const name = (s.name || "").trim();
    if (!url || !name) return;
    const key = url.toLowerCase();
    if (seenUrl.has(key)) return;
    seenUrl.add(key);
    const title = (s.title || "").trim() || undefined;
    sources.push({ name, title, url, host: receiptHost(url), primary });
  };
  add({ name: card.primarySourceName, url: card.primarySourceUrl }, true);
  for (const s of card.supportingSources || []) add(s as Source, false);
  if (!sources.length) return null;

  const hosts = new Set(sources.map((s) => s.host).filter(Boolean));
  const trustDomains = card.trust?.sourcing?.independentDomains;
  const independentDomains = Math.max(
    hosts.size,
    typeof trustDomains === "number" ? trustDomains : 0,
  );
  const strength: ReceiptStrength =
    independentDomains >= 3 ? "corroborated" : independentDomains === 2 ? "sourced" : "single";

  return {
    sources,
    independentDomains,
    primaryCount: card.trust?.sourcing?.primaryCount,
    attributed: card.trust?.sourcing?.attributed,
    imageNote: imageProvenance(card.imageUrl),
    strength,
  };
}

/** The one-line receipt summary shown on the chip ("3 sources · 3 domains"). */
export function receiptSummary(receipt: Receipt): string {
  const n = receipt.sources.length;
  const d = receipt.independentDomains;
  const src = `${n} source${n === 1 ? "" : "s"}`;
  return d > 1 ? `${src} · ${d} domains` : src;
}
